import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  FileText,
  AlertCircle,
  TrendingUp,
  ThumbsUp,
  Lightbulb,
  MessageSquare,
  ArrowRight,
  Sparkles,
  Home,
  CheckCircle,
  ArrowLeft,
  GitCompare
} from "lucide-react";

export default function ScriptResults() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await fetch(`${API}/scripts/${analysisId}`, {
          credentials: "include"
        });
        if (response.ok) {
          setResult(await response.json());
        } else {
          toast.error("Script analysis not found");
          navigate("/scripts/history");
        }
      } catch (error) {
        toast.error("Failed to load analysis");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [analysisId, navigate]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold mb-2">Analysis Not Found</h2>
        <Link to="/scripts/history"><Button>View History</Button></Link>
      </div>
    );
  }

  const analysis = result.analysis;

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="script-results-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Badge variant="secondary" className="mb-2">Script Analysis</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">
            {(result.script_type || "Script").replace(/_/g, " ")} Analysis
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/scripts/compare", { state: { originalScript: result.script_text, scriptType: result.script_type } })}
            data-testid="compare-script-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" /> Compare with New Version
          </Button>
          <Link to="/scripts/history">
            <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> History</Button>
          </Link>
          <Link to="/scripts">
            <Button variant="outline" data-testid="new-analysis-btn">
              <Sparkles className="w-4 h-4 mr-2" /> New Analysis
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button><Home className="w-4 h-4 mr-2" /> Dashboard</Button>
          </Link>
        </div>
      </div>

      {/* Score Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="relative overflow-hidden" data-testid="script-score-card">
          <div className={`absolute top-0 left-0 right-0 h-1 ${
            (analysis.overall_score || result.score) >= 80 ? "bg-green-500" :
            (analysis.overall_score || result.score) >= 60 ? "bg-yellow-500" : "bg-red-500"
          }`} />
          <CardContent className="p-6 text-center">
            <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${getScoreColor(analysis.overall_score || result.score)}`} />
            <p className={`font-heading text-5xl font-bold ${getScoreColor(analysis.overall_score || result.score)}`}>
              {analysis.overall_score || result.score}
            </p>
            <p className="text-muted-foreground mt-1">Script Score</p>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed">{analysis.summary}</p>
          </CardContent>
        </Card>
      </div>

      {/* Original Script */}
      {result.script_text && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" /> Original Script
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[250px]">
              <pre className="text-sm whitespace-pre-wrap leading-relaxed font-sans text-muted-foreground">
                {result.script_text}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Metrics */}
      {analysis.metrics && Object.keys(analysis.metrics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Script Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(analysis.metrics).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</span>
                    <span className={`font-mono font-bold ${getScoreColor(value)}`}>{value}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strengths & Improvements */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-green-500">
              <ThumbsUp className="w-5 h-5" /> Strengths
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.strengths?.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                  <span className="text-sm">{s}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
              <Lightbulb className="w-5 h-5" /> Areas to Improve
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.areas_for_improvement?.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                  <span className="text-sm">{a}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Line-by-Line Feedback */}
      {analysis.line_by_line_feedback?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" /> Line-by-Line Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {analysis.line_by_line_feedback.map((item, i) => (
              <div key={i} className="p-4 rounded-lg bg-muted/50 space-y-2">
                <p className="text-sm italic text-muted-foreground">&ldquo;{item.line}&rdquo;</p>
                <p className="text-sm font-medium">{item.feedback}</p>
                {item.suggestion && (
                  <div className="text-sm text-primary bg-primary/5 rounded-md p-2">
                    <span className="font-medium">Suggested:</span> {item.suggestion}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Improved Version */}
      {analysis.improved_version && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Improved Version
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px] w-full rounded-md border">
              <div className="p-4">
                <pre className="whitespace-pre-wrap text-sm font-mono text-muted-foreground leading-relaxed">
                  {analysis.improved_version}
                </pre>
              </div>
            </ScrollArea>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                navigator.clipboard.writeText(analysis.improved_version);
                toast.success("Improved script copied!");
              }}
            >
              Copy Improved Script
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {analysis.recommendations?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-primary" /> Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.recommendations.map((rec, i) => (
                <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-sm">{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
